/**
 *  Announcement bar — countdown opzionale e chiusura persistente.
 *
 *  <announcement-countdown> supporta tre modalità:
 *    fixed     — conto alla rovescia verso una data/ora assoluta (data-target, ISO con offset)
 *    daily     — ogni giorno verso la stessa ora nel fuso del negozio (data-daily-time)
 *    evergreen — N minuti dalla prima visita del singolo visitatore (data-minutes)
 *
 *  A zero: hide (nasconde la barra), freeze (resta a 00), message (mostra un testo alternativo).
 */
(function () {
  'use strict';

  function parseOffset(value) {
    var match = /^([+-])(\d{2}):?(\d{2})$/.exec((value || '').trim());
    if (!match) return 0;
    return (match[1] === '-' ? -1 : 1) * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
  }

  // Data il cui orario "locale" corrisponde all'orologio da muro del negozio.
  function shopWallClock(offsetMinutes) {
    var now = new Date();
    return new Date(now.getTime() + (offsetMinutes + now.getTimezoneOffset()) * 60000);
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function storage(kind) {
    try {
      var store = kind === 'session' ? window.sessionStorage : window.localStorage;
      store.setItem('__tm_probe', '1');
      store.removeItem('__tm_probe');
      return store;
    } catch (e) {
      return null;
    }
  }

  if (!customElements.get('announcement-countdown')) {
    class AnnouncementCountdown extends HTMLElement {
      connectedCallback() {
        this.offset = parseOffset(this.dataset.offset);
        this.mode = this.dataset.mode || 'fixed';
        this.onEnd = this.dataset.onEnd || 'freeze';
        this.format = this.dataset.format || 'auto';
        this.bar = this.closest('.announcement-bar');

        this.fields = {
          days: this.querySelector('[data-unit="days"]'),
          hours: this.querySelector('[data-unit="hours"]'),
          minutes: this.querySelector('[data-unit="minutes"]'),
          seconds: this.querySelector('[data-unit="seconds"]')
        };

        this.target = this.resolveTarget();
        if (this.target === null) return;

        this.ended = false;
        this.tick();
        this.timer = setInterval(this.tick.bind(this), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      resolveTarget() {
        if (this.mode === 'evergreen') return this.evergreenTarget();
        if (this.mode === 'daily') return this.dailyTarget();

        var parsed = Date.parse(this.dataset.target || '');
        return isNaN(parsed) ? null : parsed;
      }

      // Prossima occorrenza di HH:MM nel fuso del negozio: oggi se non è ancora passata, domani altrimenti.
      dailyTarget() {
        var time = (this.dataset.dailyTime || '00:00').split(':');
        var hours = parseInt(time[0], 10) || 0;
        var minutes = parseInt(time[1], 10) || 0;
        var wall = shopWallClock(this.offset);

        var utc = Date.UTC(wall.getFullYear(), wall.getMonth(), wall.getDate(), hours, minutes, 0);
        var target = utc - this.offset * 60000;

        if (target <= Date.now()) target += 86400000;
        return target;
      }

      // Countdown personale: parte alla prima visita e vive nel browser del visitatore.
      evergreenTarget() {
        var minutes = parseInt(this.dataset.minutes, 10) || 0;
        if (!minutes) return null;

        var store = storage('local');
        var key = 'tm_ab_evergreen_' + (this.dataset.key || 'default');
        var started = store ? parseInt(store.getItem(key), 10) : NaN;

        if (isNaN(started) || started > Date.now()) {
          started = Date.now();
          if (store) store.setItem(key, String(started));
        }

        var target = started + minutes * 60000;

        // Scaduto e riavvio richiesto: riparte da adesso per il prossimo visitatore ricorrente.
        if (target <= Date.now() && this.dataset.restart === 'true') {
          started = Date.now();
          if (store) store.setItem(key, String(started));
          target = started + minutes * 60000;
        }

        return target;
      }

      tick() {
        var distance = this.target - Date.now();

        if (distance <= 0) {
          this.finish();
          return;
        }

        var days = Math.floor(distance / 86400000);
        var hours = Math.floor((distance % 86400000) / 3600000);
        var minutes = Math.floor((distance % 3600000) / 60000);
        var seconds = Math.floor((distance % 60000) / 1000);

        // "auto": la colonna giorni sparisce nell'ultimo giorno, le altre restano invariate.
        if (this.format === 'auto' && this.fields.days) {
          this.fields.days.closest('.announcement-countdown--unit').hidden = days === 0;
        }
        if (this.format === 'hours' && days > 0) {
          hours += days * 24;
          days = 0;
        }

        this.write('days', days);
        this.write('hours', hours);
        this.write('minutes', minutes);
        this.write('seconds', seconds);
      }

      write(unit, value) {
        var field = this.fields[unit];
        if (field) field.textContent = pad(value);
      }

      finish() {
        if (this.ended) return;
        this.ended = true;
        clearInterval(this.timer);

        this.write('days', 0);
        this.write('hours', 0);
        this.write('minutes', 0);
        this.write('seconds', 0);

        if (this.onEnd === 'hide' && this.bar) {
          this.bar.setAttribute('hidden', '');
          window.dispatchEvent(new Event('resize'));
          return;
        }

        if (this.onEnd === 'message') {
          var replacement = this.bar && this.bar.querySelector('.announcement-bar--ended');
          if (replacement) {
            var live = this.bar.querySelector('.announcement-bar--live');
            if (live) live.hidden = true;
            replacement.hidden = false;
            window.dispatchEvent(new Event('resize'));
          }
        }
      }
    }

    customElements.define('announcement-countdown', AnnouncementCountdown);
  }

  // Scorrimento verticale della riga intera.
  // Non è un'opzione di Flickity, che lavora in orizzontale: qui le righe stanno
  // sovrapposte nella stessa cella di griglia e si muovono di traslazione.
  if (!customElements.get('announcement-rotator')) {
    class AnnouncementRotator extends HTMLElement {
      connectedCallback() {
        this.righe = [].slice.call(this.querySelectorAll('.announcement-rotator__row'));
        if (!this.righe.length) return;

        this.bar = this.closest('.announcement-bar');
        this.applicaColori(this.righe[0]);

        if (this.righe.length < 2) return;

        this.indice = 0;
        this.disegna();

        // 'false' significa rotazione spenta: il messaggio resta fermo.
        if (this.dataset.intervallo === 'false') return;
        var ms = parseInt(this.dataset.intervallo, 10);
        this.ms = !ms || ms < 1500 ? 5000 : ms;

        this.avvia = this.avvia.bind(this);
        this.ferma = this.ferma.bind(this);
        this.suVisibilita = this.suVisibilita.bind(this);

        this.addEventListener('mouseenter', this.ferma);
        this.addEventListener('mouseleave', this.avvia);
        this.addEventListener('focusin', this.ferma);
        this.addEventListener('focusout', this.avvia);
        document.addEventListener('visibilitychange', this.suVisibilita);

        this.avvia();
      }

      disconnectedCallback() {
        this.ferma();
        document.removeEventListener('visibilitychange', this.suVisibilita);
      }

      // A scheda nascosta il timer continuerebbe ad accumulare cambi inutili.
      suVisibilita() {
        if (document.hidden) this.ferma();
        else this.avvia();
      }

      avvia() {
        if (this.timer || !this.ms) return;
        this.timer = setInterval(this.avanti.bind(this), this.ms);
      }

      ferma() {
        clearInterval(this.timer);
        this.timer = null;
      }

      // Colori del singolo messaggio, come nel carosello. Vanno messi sulla BARRA e
      // non sulla riga: lo sfondo che si vede è quello della barra, la riga è
      // trasparente. Le variabili le stampa già la sezione per il primo messaggio,
      // quindi qui si cambiano solo dal secondo in poi.
      // Senza data-color/data-bg i colori per messaggio sono spenti: non si tocca
      // niente e la barra tiene il colore delle impostazioni del tema.
      applicaColori(riga) {
        if (!this.bar || !riga) return;
        var testo = riga.dataset.color;
        var sfondo = riga.dataset.bg;
        if (sfondo) this.bar.style.setProperty('--color-announcement-bar-bg', sfondo);
        if (testo) this.bar.style.setProperty('--color-announcement-bar-text', testo);
      }

      disegna() {
        for (var i = 0; i < this.righe.length; i++) {
          var attiva = i === this.indice;
          this.righe[i].dataset.stato = attiva ? 'attivo' : i < this.indice ? 'sopra' : 'sotto';
          this.righe[i].setAttribute('aria-hidden', attiva ? 'false' : 'true');
        }
        this.applicaColori(this.righe[this.indice]);
      }

      avanti() {
        var uscente = this.righe[this.indice];
        var prossimo = (this.indice + 1) % this.righe.length;
        var entrante = this.righe[prossimo];

        // Tornando alla prima riga l'entrante sarebbe 'sopra' e scenderebbe:
        // la si riporta sotto senza transizione, così il verso resta sempre lo stesso.
        entrante.style.transition = 'none';
        entrante.dataset.stato = 'sotto';
        void entrante.offsetHeight;
        entrante.style.transition = '';

        uscente.dataset.stato = 'sopra';
        uscente.setAttribute('aria-hidden', 'true');
        entrante.dataset.stato = 'attivo';
        entrante.setAttribute('aria-hidden', 'false');
        this.applicaColori(entrante);
        this.indice = prossimo;
      }
    }

    customElements.define('announcement-rotator', AnnouncementRotator);
  }

  // Larghezza della colonna messaggi.
  // Serve perché Flickity ha bisogno di una larghezza DEFINITA: con una fissa
  // (500px) un messaggio corto lasciava un buco fra testo e countdown.
  // Qui si misura il messaggio più lungo su un banco fuori schermo, senza
  // toccare gli originali, e si passa la misura al CSS.
  function misuraMessaggi(bar) {
    var items = bar.querySelectorAll('.announcement-bar--item');
    if (!items.length) return;

    var banco = document.createElement('div');
    banco.setAttribute('aria-hidden', 'true');
    banco.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:nowrap';
    bar.appendChild(banco);

    var massimo = 0;
    for (var i = 0; i < items.length; i++) {
      var copia = items[i].cloneNode(true);
      // Flickity mette position:absolute e una larghezza sugli originali:
      // sulla copia vanno annullate, altrimenti si misura il contenitore.
      copia.style.cssText = 'position:static;display:inline-flex;width:auto;max-width:none;white-space:nowrap';
      banco.appendChild(copia);
      massimo = Math.max(massimo, copia.offsetWidth);
      banco.removeChild(copia);
    }

    bar.removeChild(banco);
    if (massimo > 0) bar.style.setProperty('--ab-messaggi', Math.ceil(massimo) + 'px');
  }

  function aggiornaLarghezze() {
    var barre = document.querySelectorAll('.announcement-bar--has-extras');
    for (var i = 0; i < barre.length; i++) misuraMessaggi(barre[i]);
  }

  var attesa;
  function suRidimensiona() {
    clearTimeout(attesa);
    attesa = setTimeout(aggiornaLarghezze, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', aggiornaLarghezze);
  } else {
    aggiornaLarghezze();
  }
  // I font web cambiano la larghezza del testo: si rimisura quando sono pronti.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(aggiornaLarghezze).catch(function () {});
  }
  window.addEventListener('resize', suRidimensiona, { passive: true });

  // Chiusura persistente. Lo stato viene già letto prima del paint dallo script inline
  // stampato nella sezione: qui gestiamo solo il click e la scrittura della memoria.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('.announcement-bar--close');
    if (!button) return;

    var bar = button.closest('.announcement-bar');
    if (!bar) return;

    bar.setAttribute('hidden', '');

    var memory = bar.dataset.dismissMemory || 'session';
    var store = storage(memory === 'session' ? 'session' : 'local');

    if (store) {
      var key = 'tm_ab_dismiss_' + (bar.dataset.dismissKey || 'default');
      if (memory === 'session') {
        store.setItem(key, '1');
      } else if (memory === 'forever') {
        store.setItem(key, '-1');
      } else {
        var days = parseInt(memory, 10) || 1;
        store.setItem(key, String(Date.now() + days * 86400000));
      }
    }

    // header.js tiene l'altezza della barra in --announcement-height: va ricalcolata.
    window.dispatchEvent(new Event('resize'));
  });
})();
